/**
 * Mapping from legacy/internal capability names to actual backend keys
 * This ensures compatibility between the code and backend API
 */
const CAPABILITY_KEY_MAP = {
  // Script generation
  'script_generation': 'script_from_concept',
  'script': 'script_from_concept',
  
  // Character generation
  'character_generation': 'character_profiles',
  'characters': 'character_profiles',
  
  // Image generation (text to image)
  'professional_image': 'Text to image',
  'fast_image': 'Text to image',
  'image': 'Text to image',
  'image_generation': 'Text to image',
  'text_to_image': 'Text to image',
  'cinematic_image': 'Text to image',
  
  // Image reference to image
  'reference_image': 'Image reference to image',
  'image_ref_to_image': 'Image reference to image',
  'quick_image': 'Image reference to image',
  
  // Inpainting / Image editing
  'inpainting': 'Inpainting',
  'image_editing': 'Inpainting',
  'inpaint': 'Inpainting',
  'edit_image': 'Inpainting',

  // Image upscaling
  'image_upscale': 'Image upscaling',
  'upscale': 'Image upscaling',
  'upscaling': 'Image upscaling',

  // Video generation
  'text_to_video': 'Text to video',
  'image_to_video': 'Image to video',
  'video_generation': 'Text to video',
  
  // Video editing
  'video_editing': 'Video editing',

  // Motion control
  'motion_control': 'Motion control',

  // Start-end frame videos
  'start_end_frame': 'start-end-frame videos',

  // References-to-video (panel-based generation)
  'references_to_video': 'references-to-video',
  'references to video': 'references-to-video',
  'referencesToVideo': 'references-to-video',
  'reference_to_video': 'references-to-video',

  // Prompts
  'video_prompt': 'video_prompt',
  'frame_prompts': 'frame_prompts',
  
  // Reasoning
  'reasoning': 'reasoning',
  'ai_reasoning': 'reasoning',

  // Speech to text / Audio transcription
  'speech_to_text': 'transcribe-audio2txt',
  'audio_to_text': 'transcribe-audio2txt',
  'transcription': 'transcribe-audio2txt',
  'stt': 'transcribe-audio2txt',
  'transcribe_audio': 'transcribe-audio2txt',
  'Audio Transcription': 'transcribe-audio2txt'
};

/**
 * Get selected models from localStorage
 * @returns {Object} Selected models by capability key
 */
export function getSelectedModels() {
  try {
    const stored = localStorage.getItem('selectedAIModels');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to get selected models:', error);
    return {};
  }
}

/**
 * Get model key for a specific capability
 * Maps internal capability names to actual backend keys
 * @param {string} capabilityKey - Capability key (e.g., 'script_generation', 'fast_image')
 * @returns {string|null} Model key or null if not selected
 */
export function getModelForCapability(capabilityKey) {
  const selectedModels = getSelectedModels();
  
  // First try the exact key
  if (selectedModels[capabilityKey]) {
    return selectedModels[capabilityKey];
  }
  
  // Then try the mapped key
  const mappedKey = CAPABILITY_KEY_MAP[capabilityKey];
  if (mappedKey && selectedModels[mappedKey]) {
    return selectedModels[mappedKey];
  }
  
  // Log for debugging
  console.log(`🔍 Model lookup for '${capabilityKey}':`, {
    directMatch: selectedModels[capabilityKey],
    mappedKey: mappedKey,
    mappedMatch: mappedKey ? selectedModels[mappedKey] : null,
    allSelected: Object.keys(selectedModels)
  });
  
  return null;
}

/**
 * Clear selected models
 */
export function clearSelectedModels() {
  localStorage.removeItem('selectedAIModels');
  localStorage.removeItem('aiCapabilityTiers');
}

/**
 * Get stored capability tier data (set by AIToolkitSelector on load)
 * @returns {Object} { capKey: { premium: [...], standard: [...], economy: [...] } }
 */
export function getCapabilityTiers() {
  try {
    const stored = localStorage.getItem('aiCapabilityTiers');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Get fallback models for a capability ordered by: economy → standard → premium.
 * Excludes the currently selected (or already-tried) model.
 * Each entry: { key: string, tier: string }
 *
 * @param {string} capabilityKey - The capability key (e.g. 'Image to video')
 * @param {string|null} excludeModelKey - Model key to exclude from the list
 * @param {string|null} tierCap - Optional cap: only return models at-or-below
 *   this tier. 'economy' → economy only. 'standard' → economy + standard.
 *   'premium' or null → all tiers. This prevents an Economy-tier user from
 *   silently escalating to a Premium model on transient failure.
 * @returns {{ key: string, tier: string }[]}
 */
export function getModelsInFallbackOrder(capabilityKey, excludeModelKey = null, tierCap = null) {
  const tiers = getCapabilityTiers();

  // Resolve the actual capability key through the mapping
  const resolvedKey = CAPABILITY_KEY_MAP[capabilityKey] || capabilityKey;

  // Find tier data — try both the original key and the resolved key
  const tierData = tiers[capabilityKey] || tiers[resolvedKey] || {};

  // Fallback ordering: economy → standard → premium
  const TIER_ORDER = ['economy', 'standard', 'premium'];
  // When a `tierCap` is provided, slice the order so we never escalate
  // beyond the user's selected tier.
  const capIdx = tierCap ? TIER_ORDER.indexOf(tierCap) : (TIER_ORDER.length - 1);
  const allowedTiers = capIdx >= 0 ? TIER_ORDER.slice(0, capIdx + 1) : TIER_ORDER;

  const ordered = [];
  for (const tier of allowedTiers) {
    const models = tierData[tier] || [];
    for (const m of models) {
      const key = m.key || m.id || m.model_key || m;
      if (key && key !== excludeModelKey) {
        ordered.push({ key, tier });
      }
    }
  }

  return ordered;
}

/**
 * Feb 2026 — Tier selection has been fully retired from the wizard.
 * Users now pick their AI models directly via the AI Toolkit step, so
 * capping fallback chains by tier is no longer meaningful. This function
 * always returns null (the legacy "unlimited fallback" behaviour). Left
 * in place so callers in `aiService.js` don't need to be rewritten.
 *
 * We also proactively purge any pre-existing `selectedAITier` from
 * localStorage on module load — this clears the tier cap for users who
 * had one persisted from earlier builds.
 *
 * @returns {null}
 */
try { localStorage.removeItem('selectedAITier'); } catch { /* noop */ }

export function getSelectedTier() {
  return null;
}
